import { useMemo, useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Editor from "@monaco-editor/react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";

import DualListBox from "react-dual-listbox";
import "react-dual-listbox/lib/react-dual-listbox.css";
import { useSelector } from "react-redux";

const dualListStyles = {
  "& .rdl-control": {
    height: "41vh",
  },
};

function AddCollectionModal({
  open,
  onClose,
  onConfirm,
  dataRecords,
  savedData,
}) {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem("editorMode") || "simple";
  });

  const [items, setItems] = useState([]);
  const [name, setName] = useState("");

  useEffect(() => {
    localStorage.setItem("editorMode", mode);
  }, [mode]);

  useEffect(() => {
    if (savedData && open) {
      setName(savedData.name || "");
      
      setItems(savedData.dataIds || []);
    }
  }, [savedData, open]);

  const uniqueIdentifiers = useMemo(
    () => dataRecords.map((r) => ({ id: r.id, identifier: r.identifier })),
    [dataRecords]
  );

  const providerRef = useRef(null);

  const handleOnMount = (editor, monaco) => {
    if (providerRef.current) {
      providerRef.current.dispose();
    }

    providerRef.current = monaco.languages.registerCompletionItemProvider(
      "plaintext",
      {
        provideCompletionItems: (model, position) => {
          const suggestions = uniqueIdentifiers.map((item) => ({
            label: item.identifier,
            kind: monaco.languages.CompletionItemKind.Value,
            insertText: item.identifier,
            detail: "Data Record",

            range: {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: model.getWordUntilPosition(position).startColumn,
              endColumn: position.column,
            },
          }));
          return { suggestions };
        },
      }
    );
  };

  useEffect(() => {
    return () => {
      if (providerRef.current) providerRef.current.dispose();
    };
  }, []);

  const editorValue = useMemo(() => {
    return items.map((i) => i.identifier).join("\n");
  }, [items]);

  const handleEditorChange = (value) => {
    if (value === undefined) return;

    const lines = [
      ...new Set(
        value
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line !== "")
      ),
    ];

    const matchedItems = lines
      .map((line) =>
        uniqueIdentifiers.find((record) => record.identifier === line)
      )
      .filter(Boolean);

    const currentIds = items.map((i) => i.id).join(",");
    const matchedIds = matchedItems.map((i) => i.id).join(",");

    if (currentIds !== matchedIds) {
      setItems(matchedItems);
    }
  };
  const handleReset = () => {
    setItems([]);
    setName("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleReset}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: { width: {xs:"95vw",md:"80vw"} , height: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <TextField
          variant="standard"
          placeholder="Untitled Collection"
          value={name}
          onChange={(e) => setName(e.target.value)}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: "1.5rem", fontWeight: "bold", width: "400px" },
          }}
        />

        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(e, m) => m && setMode(m)}
          size="small"
        >
          <ToggleButton value="simple" sx={{ textTransform: "none" }}>
            Simple Editor
          </ToggleButton>
          <ToggleButton value="dual" sx={{ textTransform: "none" }}>
            Dual Listbox
          </ToggleButton>
        </ToggleButtonGroup>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          p: mode === "dual" ? 4 : 2,
        }}
      >
        {mode === "simple" ? (
          <Box
            sx={{
              p: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}
            >
              Collection Data ({items.length} items)
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: "500px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <Editor
                height="100%"
                key="monaco-simple-editor" 
                defaultLanguage="plaintext"
                value={editorValue}
                onChange={handleEditorChange}
                options={{
                  lineNumbers: "on",
                  minimap: { enabled: false },
                  fontSize: 14,
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  padding: { top: 10 },
                  wordWrap: "on",
                  suggestSelection: "first",
                  acceptSuggestionOnEnter: "on",
                }}
                onMount={handleOnMount}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ ...dualListStyles }}>
            <DualListBox
              options={uniqueIdentifiers.map((i) => ({
                value: i.id,
                label: i.identifier,
              }))}
              selected={items.map((i) => i.id)}
              onChange={(val) => {
                const selected = uniqueIdentifiers.filter((opt) =>
                  val.includes(opt.id)
                );
                setItems(selected);
              }}
              canFilter
              showHeaderLabels
              lang={{
                availableHeader: "Site Data",
                selectedHeader: "Collection Data",
              }}
              icons={{
                moveToSelected: <ChevronRightIcon />,
                moveToAvailable: <ChevronLeftIcon />,
                moveAllToSelected: <KeyboardDoubleArrowRightIcon />,
                moveAllToAvailable: <KeyboardDoubleArrowLeftIcon />,
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleReset}
          variant="contained"
          sx={{
            textTransform: "none",
            background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
            color: "#fff",
            boxShadow: "none",
            "&:hover": {
              background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
              opacity: 0.95,
              boxShadow: "none"
            },
            "&.Mui-disabled": {
              background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
              opacity: 0.5,
              color: "#fff"
            }
          }}

        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onConfirm(
              savedData ? { id: savedData.id, name, items } : { name, items }
            );
            handleReset();
          }}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: "rgb(90, 28, 47)",
            "&:hover": {
              backgroundColor: "rgb(70, 20, 35)",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(90, 28, 47, 0.4)",
              color: "#fff",
            },
          }}
          disabled={items.length === 0 || !name.trim()}
        >
          Save Collection ({items.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddCollectionModal;
